import { createSlice } from "@reduxjs/toolkit";
import { LEGACY_SESSION_KEYS, STORAGE_KEYS } from "../../constants";
import { login, fetchMe } from "../Thunks/AuthThunks";

// Initialize auth state from localStorage if available
const getInitialAuthState = () => {
  try {
    const authToken = localStorage.getItem("authToken");
    if (authToken) {
      const tokenData = JSON.parse(authToken);
      // If token exists, we consider user as authenticated (user shape from login payload or token bundle)
      return {
        user: tokenData.user || tokenData || null, // Support both formats
        me: null,
        /** `false` until GET /users/me settles — avoids permission UI flashing 403 before role.permissions load */
        profileResolved: false,
        loading: {
          login: false,
          fetchMe: false,
        },
        error: null,
      };
    }
  } catch (error) {
    console.error("Error initializing auth state:", error);
  }

  return {
    user: null,
    me: null,
    profileResolved: true,
    loading: {
      login: false,
      fetchMe: false,
    },
    error: null,
  };
};

const initialState = getInitialAuthState();

export const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null;
      state.me = null;
      state.profileResolved = true;
      state.error = null;
      try {
        localStorage.removeItem(STORAGE_KEYS.USER_ROLE_ID);
        sessionStorage.removeItem(LEGACY_SESSION_KEYS.USER_ROLE_JSON);
      } catch {
        /* ignore */
      }
    },
    clearAuthError: (state) => {
      state.error = null;
    },
    /** Re-read user from localStorage authToken (no GET /auth/profile). */
    hydrateUserFromStorage: (state) => {
      try {
        const raw = localStorage.getItem("authToken");
        if (!raw) return;
        const tokenData = JSON.parse(raw);
        state.user = tokenData?.user ?? tokenData ?? null;
      } catch {
        /* ignore */
      }
    },
  },
  extraReducers: (builder) => {
    // Login
    builder
      .addCase(login.pending, (state) => {
        state.loading.login = true;
        state.error = null;
      })
      .addCase(login.fulfilled, (state, action) => {
        state.loading.login = false;
        state.user = action.payload;
        state.profileResolved = false;
      })
      .addCase(login.rejected, (state, action) => {
        state.loading.login = false;
        state.error = action.payload;
      });

    // GET /users/me — profile in `me`; merged into `user` for existing selectors
    builder
      .addCase(fetchMe.pending, (state) => {
        state.loading.fetchMe = true;
        state.error = null;
      })
      .addCase(fetchMe.fulfilled, (state, action) => {
        state.loading.fetchMe = false;
        state.error = null;
        state.profileResolved = true;
        state.me = action.payload;
        const prev = state.user && typeof state.user === "object" ? state.user : {};
        state.user = { ...prev, ...action.payload };
      })
      .addCase(fetchMe.rejected, (state, action) => {
        state.loading.fetchMe = false;
        state.profileResolved = true;
        state.error = action.payload;
      });
  },
});

export const { logout, clearAuthError, hydrateUserFromStorage } = authSlice.actions;
export default authSlice.reducer;

export const selectAuthMe = (state) => state.auth?.me ?? null;
export const selectAuthUserRole = (state) => state.auth?.me?.role ?? null;

/** Logged-in user id from GET /users/me or login token (for default assignee, etc.). */
export const selectAuthUserId = (state) => {
  const u = state.auth?.me ?? state.auth?.user ?? null;
  if (!u || typeof u !== "object") return null;
  const id = u.id ?? u.userId;
  return id != null && String(id).trim() !== "" ? String(id).trim() : null;
};
